import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileCode, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import BZAPIService from '../../services/BZAPIService';

const ExportModal = ({ isOpen, toggle, selectedProject, currentUserEmail, canCreateExport }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const [exports, setExports] = useState([]);
  const [reportName, setReportName] = useState('');
  const [author, setAuthor] = useState(currentUserEmail || '');
  const [downloadingJobId, setDownloadingJobId] = useState(null);

  const loadExports = useCallback(async () => {
    try {
      setIsLoadingExports(true);
      const response = await BZAPIService.bsaListExports({ bsaProjectId: selectedProject.id });
      if (response?.valid && response?.jobs) {
        setExports(response.jobs);
      } else {
        setExports([]);
      }
    } catch (error) {
      console.error('Failed to load exports:', error);
      toast.error('Failed to load export history');
      setExports([]);
    } finally {
      setIsLoadingExports(false);
    }
  }, [selectedProject?.id]);

  // Load exports on mount and when project changes
  useEffect(() => {
    if (isOpen && selectedProject?.id) {
      loadExports();
      setReportName(selectedProject?.name || ''); // Default to project name
      setAuthor(currentUserEmail || '');
    }
  }, [isOpen, selectedProject?.id, selectedProject?.name, currentUserEmail, loadExports]);

  const handleCreateExport = async () => {
    if (!reportName.trim()) {
      toast.error('Report name is required');
      return;
    }
    if (!author.trim()) {
      toast.error('Author is required');
      return;
    }

    try {
      setIsCreating(true);
      const exportData = {
        reportName: reportName.trim(),
        author: author.trim(),
        vendorId: selectedProject.vendorId,
        bsaProjectId: selectedProject.id
      };

      await BZAPIService.bsaStartExport(exportData);
      toast.success('Export job started successfully');
      setReportName(selectedProject?.name || '');
      setAuthor(currentUserEmail || '');
      // Reload the exports list
      await loadExports();
    } catch (error) {
      console.error('Failed to start export:', error);
      toast.error('Failed to start export job');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (jobId, format) => {
    try {
      setDownloadingJobId(jobId);
      // Fetch the single job with presigned URLs
      const response = await BZAPIService.bsaListExports({ jobId });

      if (!response?.valid || !response?.job) {
        toast.error('Failed to get download URL');
        return;
      }

      const job = response.job;
      const url = format === 'pdf' ? job.pdfUrl : job.htmlUrl;

      if (!url) {
        toast.error(`${format.toUpperCase()} file is not available yet`);
        return;
      }

      // Open the presigned URL in a new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloadingJobId(null);
    }
  };

  const getStatusBadge = status => {
    if (status === 'COMPLETED') {
      return <span className="badge bg-success">Completed</span>;
    }
    if (status === 'IN_PROGRESS') {
      return <span className="badge bg-primary">In Progress</span>;
    }
    if (status === 'FAILED') {
      return <span className="badge bg-danger">Failed</span>;
    }
    return <span className="badge bg-secondary">{status}</span>;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Export BugZero Plan Project Report</ModalHeader>
      <ModalBody>
        {/* Create New Export Form */}
        {canCreateExport && (
          <>
            <h6>Create New Export</h6>
            <Form>
              <FormGroup>
                <Label for="reportName">Report Name *</Label>
                <Input
                  type="text"
                  id="reportName"
                  value={reportName}
                  onChange={e => setReportName(e.target.value)}
                  placeholder="e.g., Q4 2024 Cisco Analysis"
                  maxLength={128}
                  disabled={isCreating}
                />
              </FormGroup>
              <FormGroup>
                <Label for="author">Author *</Label>
                <Input
                  type="text"
                  id="author"
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                  placeholder="Your email address"
                  maxLength={128}
                  disabled={isCreating}
                />
              </FormGroup>
              <Button
                color="primary"
                onClick={handleCreateExport}
                disabled={isCreating || !reportName.trim() || !author.trim()}
                className="btn-sm"
              >
                {isCreating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating Export...
                  </>
                ) : (
                  'Start Export'
                )}
              </Button>
            </Form>
            <hr className="my-4" />
          </>
        )}

        {/* Export History */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Export History</h6>
          <Button color="link" size="sm" onClick={loadExports} disabled={isLoadingExports}>
            <FontAwesomeIcon icon={faRefresh} className={isLoadingExports ? 'fa-spin' : ''} />
            {' Refresh'}
          </Button>
        </div>

        {isLoadingExports ? (
          <div className="text-center my-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading export history...</p>
          </div>
        ) : exports.length === 0 ? (
          <Alert color="info">No export jobs have been created for this project yet.</Alert>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Report Name</th>
                <th>Author</th>
                <th>Created</th>
                <th>Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {exports.map(exportJob => (
                <tr key={exportJob.jobId}>
                  <td>{exportJob.reportName}</td>
                  <td>{exportJob.author}</td>
                  <td>{moment.utc(exportJob.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                  <td>
                    {getStatusBadge(exportJob.status)}
                    {exportJob.errorMessage && (
                      <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>
                        {exportJob.errorMessage}
                      </div>
                    )}
                  </td>
                  <td>
                    {exportJob.finished && exportJob.status !== 'FAILED' ? (
                      <div className="d-flex gap-2">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDownload(exportJob.jobId, 'pdf')}
                          disabled={downloadingJobId === exportJob.jobId}
                          title="Download PDF"
                        >
                          {downloadingJobId === exportJob.jobId ? (
                            <Spinner size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faFilePdf} />
                          )}
                          {' PDF'}
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => handleDownload(exportJob.jobId, 'html')}
                          disabled={downloadingJobId === exportJob.jobId}
                          title="Download HTML"
                        >
                          {downloadingJobId === exportJob.jobId ? (
                            <Spinner size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faFileCode} />
                          )}
                          {' HTML'}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted">Not ready</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-falcon-default" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedProject: PropTypes.object,
  currentUserEmail: PropTypes.string,
  canCreateExport: PropTypes.bool
};

export default ExportModal;
